import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  alpha
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Download as DownloadIcon,
  Add as AddToWalletIcon,
  Share as ShareIcon,
  ShieldOutlined as ShieldOutlinedIcon,
  PhoneInTalkOutlined as PhoneInTalkOutlinedIcon
} from '@mui/icons-material';
import { Card, CardAction } from '../types';
import { brandThemeOptions } from './brandColors';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { RootState } from '../../../store/store';
import { GetDownloadIdCard } from '../../Dashboard/DashboardAction';
import { getBufferData } from '../../../utils/Utility';
import { setDownloadIdCardContent } from '../../Dashboard/DashboardSlice';
import { resetCommonSlice } from '../../../store/common/CommonSlice';

interface InsuranceIDCardProps {
  card: Card;
  onAction?: (action: CardAction, followupMessage?: string) => void;
}

const InsuranceIDCard: React.FC<InsuranceIDCardProps> = ({ card, onAction }) => {
  const dispatch = useAppDispatch();
  const { isSuccess, isError, calledReducerType } = useAppSelector((s: RootState) => s.common);
  const { downloadIdCardContent } = useAppSelector((s: RootState) => s.dashboard);

  const findValueByLabel = React.useCallback(
    (label: string) => {
      const targetLabel = label.toLowerCase();
      for (const section of card?.sections || []) {
        const matchedValue = section?.items?.find(
          (item: any) => item?.label?.toLowerCase() === targetLabel
        )?.value;
        if (matchedValue) {
          return matchedValue;
        }
      }
      return "";
    },
    [card?.sections]
  );

  const policyIdFromSections = findValueByLabel("policyId");

  const handleDownload = () => {
    if (!policyIdFromSections) {
      alert("Policy ID not found");
      return;
    }
    dispatch(
      GetDownloadIdCard({
        policyId: policyIdFromSections,
        lobCode: "PA",
      })
    );
  };

  React.useEffect(() => {
    if (
      isSuccess &&
      calledReducerType === "GetDownloadIdCard" &&
      downloadIdCardContent
    ) {
      const bufferArray = getBufferData(downloadIdCardContent);
      const url = URL.createObjectURL(new Blob([bufferArray], { type: "application/pdf" }));
      const downloadLink = document.createElement("a");
      downloadLink.download = "Insurance ID Card.pdf";
      downloadLink.href = url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      dispatch(setDownloadIdCardContent(null));
    }
  }, [calledReducerType, isSuccess, downloadIdCardContent, dispatch]);

  React.useEffect(() => {
    if (isError && calledReducerType === "GetDownloadIdCard") {
      alert(`Error in downloading document`);
      dispatch(setDownloadIdCardContent(null));
      dispatch(resetCommonSlice());
    }
  }, [calledReducerType, isError, dispatch]);

  const handleActionClick = (action: CardAction) => {
    // Check if this is a download action
    const actionLabel = (action?.label || "").toString().toLowerCase();
    if (actionLabel.includes("download")) {
      handleDownload();
      return;
    }
    if (!onAction) return;
    onAction(action, (action as any).followup_message);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Extract vehicle information from the card sections
  const vehicleSection = card.sections.find(section => section.heading === 'Vehicle');
  const vehicleItems = vehicleSection?.items || [];

  const getItemValue = (label: string) => {
    return vehicleItems.find(item => item.label === label)?.value || '';
  };
  
  const makeModel = getItemValue('Year/Make/Model');
  const vin = getItemValue('VIN');
  const effectiveDate = getItemValue('Effective');
  const expiresDate = getItemValue('Expires');
  const policyNumber = getItemValue('Policy');
  const agency = getItemValue('Agency');
  const agencyPhone = getItemValue('Agency Phone');
  const namedInsured = getItemValue('Named Insured');
  const driver = getItemValue('Driver(s)')

  const labelSx = {
    fontWeight: 700,
    color: '#9ca3af',
    fontSize: '10.5px',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  } as const;
  const valueSx = { fontWeight: 600, fontSize: '14px', color: '#111827' } as const;

  return (
    <ThemeProvider theme={(outer) => createTheme(outer, brandThemeOptions)}>
    <Box sx={{ height: 'auto', p: { xs: 1, md: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 0,
          maxWidth: 400,
          mx: 'auto',
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: 'white',
          border: '1px solid #eceff3',
          boxShadow: (theme) => `0 14px 34px ${alpha(theme.palette.primary.main, 0.16)}`,
        }}
      >
        {/* Header with logo and title */}
        <Box sx={{
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            backgroundColor: 'white',
            borderRadius: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main',
            flexShrink: 0,
            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
          }}>
            <ShieldOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', lineHeight: 1.2 }}>
              Insurance ID Card
            </Typography>
            <Typography sx={{ fontSize: '11px', opacity: 0.9 }}>
              Auto Policy
            </Typography>
          </Box>
        </Box>

        {/* Policy Information Section */}
        <Box sx={{ p: 2, borderBottom: '1px solid #eceff3' }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={labelSx}>Policy Number</Typography>
            <Typography sx={{ ...valueSx, color: 'primary.main', fontWeight: 700, letterSpacing: 0.5 }}>
              {policyNumber}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography sx={labelSx}>Effective Date</Typography>
              <Typography sx={{ ...valueSx, fontSize: '13px' }}>
                {formatDate(effectiveDate)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={labelSx}>Expiration Date</Typography>
              <Typography sx={{ ...valueSx, fontSize: '13px' }}>
                {formatDate(expiresDate)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Vehicle Information Section */}
        <Box sx={{ p: 2, borderBottom: '1px solid #eceff3' }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={labelSx}>Vehicle Year/Make/Model</Typography>
            <Typography sx={valueSx}>{makeModel}</Typography>
          </Box>
          <Box>
            <Typography sx={labelSx}>Vehicle Identification Number (VIN)</Typography>
            <Typography sx={{ ...valueSx, fontFamily: 'monospace', letterSpacing: 0.5 }}>{vin}</Typography>
          </Box>
        </Box>

        {/* Claims Contact Section */}
        <Box sx={{
          p: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
          borderBottom: '1px solid #eceff3',
          textAlign: 'center',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.25, color: 'primary.main' }}>
            <PhoneInTalkOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#374151' }}>
              24 Hour Claims
            </Typography>
          </Box>
          <Typography
            component="a"
            href="tel:18004213535"
            sx={{ fontWeight: 800, fontSize: '20px', color: 'primary.main', letterSpacing: 0.5, textDecoration: 'none' }}
          >
            1.800.421.3535
          </Typography>
        </Box>

        {/* Insured and Driver Information */}
        <Box sx={{ p: 2, borderBottom: '1px solid #eceff3', display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={labelSx}>Named Insured</Typography>
            <Typography sx={valueSx}>{namedInsured}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={labelSx}>Driver(s)</Typography>
            <Typography sx={valueSx}>{driver}</Typography>
          </Box>
        </Box>

        {/* Agent Contact */}
        <Box sx={{ p: 2, bgcolor: '#fafbfc' }}>
          <Typography sx={{ fontSize: '11.5px', color: '#6b7280' }}>
            For questions about your policy, call your Insurance Agent.
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '13px', mt: 0.5, color: '#111827' }}>
            {agency} {agencyPhone ? `at ${agencyPhone}` : ''}
          </Typography>
        </Box>

        {/* Action Buttons */}
        {card.cta && (card.cta.primary || card.cta.secondary) && (
          <Box sx={{ p: 2, borderTop: '1px solid #eceff3' }}>
            <Stack spacing={1}>
              {card.cta.primary && (
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  startIcon={<DownloadIcon />}
                  onClick={() => handleActionClick(card.cta.primary!)}
                  sx={{
                    py: 1,
                    borderRadius: 9999,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {card.cta.primary.label}
                </Button>
              )}

              {card.cta.secondary && card.cta.secondary.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {card.cta.secondary.map((action, index) => (
                    <Button
                      key={index}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={index === 0 ? <AddToWalletIcon /> : <ShareIcon />}
                      onClick={() => handleActionClick(action)}
                      sx={{
                        flex: 1,
                        borderRadius: 9999,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: (theme) => alpha(theme.palette.secondary.main, 0.6),
                        color: 'secondary.main',
                        '&:hover': {
                          borderColor: 'secondary.main',
                          bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                        },
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
    </ThemeProvider>
  );
};

export default InsuranceIDCard;
